import { useParams } from "react-router-dom";
import { useRef, useCallback, useEffect, useState } from "react";
import { useShell, PKB_AGENT_ID } from "../../context/ShellContext";
import "./AgentHost.css";
import {
  Bot24Regular,
} from "@fluentui/react-icons";

export function AgentHost() {
  const { agentRoute } = useParams<{ agentRoute: string }>();
  const { agents, userInfo, userName, userRole, setSidebarOpen } = useShell();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  const agent = agents.find(
    (a) => a.route === agentRoute && a.agentId !== PKB_AGENT_ID
  );

  // Reset iframe-loaded flag when the agent route changes so the auth
  // handshake runs again for the new iframe.
  useEffect(() => {
    setIframeLoaded(false);
  }, [agentRoute]);

  // Collapse the sidebar whenever an agent is launched so the iframe has
  // maximum screen real estate.
  useEffect(() => {
    setSidebarOpen(false);
  }, [agentRoute, setSidebarOpen]);

  // Mark the iframe as loaded so the auth effect below can fire.
  const handleIframeLoad = useCallback(() => {
    setIframeLoaded(true);
  }, []);

  // Send AURA identity to the embedded agent once the iframe has loaded
  // AND userInfo is available. This avoids the race condition where the
  // iframe finishes loading before /.auth/me returns, which previously
  // caused the auth message to be silently skipped — leaving the embedded
  // agent on its own login screen.
  useEffect(() => {
    if (!iframeLoaded) return;
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow || !agent?.remoteUrl) return;

    const email =
      userInfo?.clientPrincipal?.claims?.find(
        (c: { typ: string; val: string }) =>
          c.typ === "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"
      )?.val ?? userInfo?.clientPrincipal?.userDetails;

    if (!email) return;

    let targetOrigin: string;
    try {
      targetOrigin = new URL(agent.remoteUrl).origin;
    } catch {
      targetOrigin = window.location.origin;
    }

    // Post the user identity to the iframe so it can auto-authenticate
    iframe.contentWindow.postMessage(
      {
        type: "aura-auth",
        email,
        displayName: userName ?? email,
        role: userRole,
      },
      targetOrigin
    );
  }, [iframeLoaded, agent?.remoteUrl, userInfo, userName, userRole]);

  // Listen for re-auth requests from embedded agents whose JWT has expired.
  // When the iframe posts { type: "rfp-auth-request" }, we re-send the
  // aura-auth message so the embedded app can obtain a fresh token without
  // forcing the user through a login page.
  useEffect(() => {
    const handleReauthRequest = (event: MessageEvent) => {
      if (event.data?.type !== "rfp-auth-request") return;

      const iframe = iframeRef.current;
      if (!iframe?.contentWindow || !agent?.remoteUrl) return;

      const email =
        userInfo?.clientPrincipal?.claims?.find(
          (c: { typ: string; val: string }) =>
            c.typ === "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"
        )?.val ?? userInfo?.clientPrincipal?.userDetails;

      if (!email) return;

      let targetOrigin: string;
      try {
        targetOrigin = new URL(agent.remoteUrl).origin;
      } catch {
        targetOrigin = window.location.origin;
      }

      iframe.contentWindow.postMessage(
        {
          type: "aura-auth",
          email,
          displayName: userName ?? email,
          role: userRole,
        },
        targetOrigin
      );
    };

    window.addEventListener("message", handleReauthRequest);
    return () => window.removeEventListener("message", handleReauthRequest);
  }, [agent?.remoteUrl, userInfo, userName, userRole]);

  // Agent not found — unknown route
  if (!agent) {
    return (
      <div className="agent-host-placeholder">
        <div className="agent-host-placeholder-content">
          <Bot24Regular className="agent-host-placeholder-icon" />
          <h2 className="agent-host-placeholder-title">Agent Not Found</h2>
          <p className="agent-host-placeholder-desc">
            The agent you are looking for does not exist or is no longer available.
          </p>
        </div>
      </div>
    );
  }

  // Agent exists but user doesn't have access
  if (!agent.hasAccess) {
    return (
      <div className="agent-host-placeholder">
        <div className="agent-host-placeholder-content">
          <Bot24Regular className="agent-host-placeholder-icon" />
          <h2 className="agent-host-placeholder-title">Access Restricted</h2>
          <p className="agent-host-placeholder-desc">
            You do not have access to <strong>{agent.name}</strong>. Please contact your administrator to request access.
          </p>
        </div>
      </div>
    );
  }

  // Agent exists and has access but no remote URL configured
  if (!agent.remoteUrl) {
    return (
      <div className="agent-host-placeholder">
        <div className="agent-host-placeholder-content">
          <Bot24Regular className="agent-host-placeholder-icon" />
          <h2 className="agent-host-placeholder-title">{agent.name}</h2>
          <p className="agent-host-placeholder-desc">
            This agent is not yet available. It is currently being configured and will be ready soon.
          </p>
        </div>
      </div>
    );
  }

  // Agent has a remote URL — load in iframe
  return (
    <div className="agent-host-iframe-wrapper">
      <iframe
        ref={iframeRef}
        className="agent-host-iframe"
        src={agent.remoteUrl}
        title={agent.name}
        allow="clipboard-read; clipboard-write"
        sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads"
        onLoad={handleIframeLoad}
      />
    </div>
  );
}
